import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Building2, Sparkles, ArrowLeft, Loader2, ChevronDown, ChevronUp, Gift, Plus, Minus, CreditCard, Video } from "lucide-react";
import { toast } from "sonner";
import axios from 'axios';
import Footer from './components/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FEATURE_LABELS = {
  diagnostico_basico: "Diagnóstico básico",
  diagnostico_financeiro: "Diagnóstico financeiro completo",
  organizacao_gastos: "Organização de gastos",
  controle_orcamento: "Controle de orçamento",
  dicas_economia: "Dicas automáticas de economia",
  atendimento_24h: "Atendimento IA 24h",
  planejamento_completo: "Planejamento financeiro completo",
  estrategias_dividas: "Estratégias para sair das dívidas",
  simulacao_renegociacao: "Simulação de renegociação",
  planejamento_mensal: "Planejamento mensal personalizado",
  alertas_acompanhamento: "Alertas e acompanhamento",
  modo_crise: "Modo Crise (orientação passo a passo)",
  analise_expansao: "Análise de Expansão",
  consultoria_humana: "Consultoria com especialista humano",
  separacao_pf_pj: "Separação completa PF/PJ",
  controle_caixa: "Controle de caixa empresarial",
  planejamento_custos: "Planejamento de custos operacionais",
  calculadora_preco: "Calculadora de precificação",
  relatorios_gerenciais: "Relatórios gerenciais",
  multi_usuarios: "Até 2 usuários (titular + 1)",
  sessao_mensal_consultor: "1 sessão mensal com consultor"
};

// Define exclusive features for each plan (features that are NEW in that plan)
const PLAN_EXCLUSIVE_FEATURES = {
  free: [
    "diagnostico_basico",
    "organizacao_gastos",
    "controle_orcamento"
  ],
  essencial: [
    "diagnostico_financeiro",
    "dicas_economia",
    "atendimento_24h"
  ],
  premium: [
    "planejamento_completo",
    "estrategias_dividas",
    "simulacao_renegociacao",
    "planejamento_mensal",
    "alertas_acompanhamento",
    "modo_crise",
    "analise_expansao",
    "consultoria_humana"
  ],
  empresarial: [
    "separacao_pf_pj",
    "controle_caixa",
    "planejamento_custos",
    "calculadora_preco",
    "relatorios_gerenciais",
    "multi_usuarios",
    "sessao_mensal_consultor"
  ]
};

// Updated feature labels
const UPDATED_FEATURE_LABELS = {
  ...FEATURE_LABELS,
  multi_usuarios: "Até 2 usuários (titular + 1)"
};

const PLAN_INCLUDES = {
  free: null,
  essencial: "Gratuito",
  premium: "Essencial",
  empresarial: "Premium"
};

const PricingPage = ({ onBack }) => {
  const { user, token, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [expandedPlans, setExpandedPlans] = useState({});
  const [creditsQuantity, setCreditsQuantity] = useState(10);
  const [consultancySessions, setConsultancySessions] = useState(1);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadPlans();
    if (token) {
      loadSubscriptionStatus();
    }
  }, [token]);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      // Include free plan and rename basico to essencial
      const allPlans = response.data.map(p => ({
        ...p,
        id: p.id === 'basico' ? 'essencial' : p.id,
        name: p.id === 'basico' ? 'Essencial' : p.name
      }));
      setPlans(allPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (planId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      toast.info('Você já está no plano gratuito!');
      return;
    }

    if (!user) {
      toast.error('Faça login para assinar um plano');
      return;
    }

    // Map essencial back to basico for the API
    const apiPlanId = planId === 'essencial' ? 'basico' : planId;

    if (user.plan === apiPlanId || (user.plan === 'basico' && planId === 'essencial')) {
      toast.info('Você já possui este plano');
      return;
    }

    setCheckoutLoading(planId);

    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan_id: apiPlanId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erro ao iniciar pagamento');
      setCheckoutLoading(null);
    }
  };

  const handleBuyCredits = async () => {
    if (!user) {
      toast.error('Faça login para comprar créditos');
      return;
    }

    if (creditsQuantity < 1) {
      toast.error('Selecione pelo menos 1 crédito');
      return;
    }

    setCheckoutLoading('credits');

    try {
      const response = await axios.post(
        `${API}/credits/purchase`,
        {
          quantity: creditsQuantity,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error buying credits:', error);
      toast.error('Erro ao iniciar compra de créditos');
      setCheckoutLoading(null);
    }
  };

  const handleBuyConsultancy = async () => {
    if (!user) {
      toast.error('Faça login para comprar consultoria');
      return;
    }

    setCheckoutLoading('consultancy');

    try {
      const response = await axios.post(
        `${API}/consultancy/purchase`,
        {
          sessions: consultancySessions,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error buying consultancy:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao iniciar compra de consultoria';
      toast.error(errorMsg);
      setCheckoutLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free':
        return <Gift className="w-5 h-5" />;
      case 'essencial':
        return <Sparkles className="w-5 h-5" />;
      case 'premium':
        return <Star className="w-5 h-5" />;
      case 'empresarial':
        return <Building2 className="w-5 h-5" />;
      default:
        return <Sparkles className="w-5 h-5" />;
    }
  };

  const getPlanGradient = (planId) => {
    switch (planId) {
      case 'free':
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
      case 'essencial':
        return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      case 'premium':
        return 'linear-gradient(135deg, #00ffff 0%, #00cccc 100%)';
      case 'empresarial':
        return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
      default:
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    }
  };

  const getExclusiveFeatures = (planId) => {
    return PLAN_EXCLUSIVE_FEATURES[planId] || [];
  };

  const getAllFeatures = (planId) => {
    // Get all features including inherited ones
    const inheritance = {
      free: ['free'],
      essencial: ['free', 'essencial'],
      premium: ['free', 'essencial', 'premium'],
      empresarial: ['free', 'essencial', 'premium', 'empresarial']
    };
    
    const planChain = inheritance[planId] || [planId];
    let allFeatures = [];
    planChain.forEach(p => {
      allFeatures = [...allFeatures, ...(PLAN_EXCLUSIVE_FEATURES[p] || [])];
    });
    return allFeatures;
  };

  const isCurrentPlan = (planId) => {
    if (planId === 'essencial' && user?.plan === 'basico') return true;
    if (planId === 'free' && (!user?.plan || user?.plan === 'free')) return true;
    return user?.plan === planId;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%', 
        gap: '1rem' 
      }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00ffff' }} />
        <p style={{ color: '#888' }}>Carregando planos...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '1.5rem', 
      height: '100%', 
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Back Button */}
      <Button 
        variant="outline" 
        onClick={onBack} 
        style={{ 
          marginBottom: '1rem',
          color: '#00ffff',
          borderColor: '#00ffff',
          width: 'fit-content',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: 'transparent'
        }}
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar ao App
      </Button>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ 
          fontSize: '1.75rem', 
          fontWeight: '700', 
          color: '#ffffff',
          marginBottom: '0.5rem'
        }}>
          Escolha seu Plano
        </h1>
        <p style={{ color: '#888', fontSize: '0.9rem' }}>
          Sem julgamento. Sem pressão. No seu tempo.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="pricing-grid" style={{ 
        display: 'grid', 
        gap: '1rem',
        maxWidth: '1200px',
        margin: '0 auto',
        flex: 1,
        width: '100%'
      }}>
        {plans.map((plan) => {
          const isExpanded = expandedPlans[plan.id];
          const exclusiveFeatures = getExclusiveFeatures(plan.id);
          const allFeatures = getAllFeatures(plan.id);
          const includesText = PLAN_INCLUDES[plan.id];
          const isFree = plan.id === 'free';
          const isPopular = plan.id === 'premium';
          const isCurrent = isCurrentPlan(plan.id);

          return (
            <div 
              key={plan.id}
              style={{
                background: '#1a1a1a',
                border: isPopular ? '2px solid #00ffff' : '2px solid #333',
                borderRadius: '12px',
                padding: '1.25rem',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: isPopular ? '0 0 25px rgba(0, 255, 255, 0.15)' : 'none',
                opacity: isFree ? 0.85 : 1
              }}
            >
              {/* Popular Badge - Improved */}
              {isPopular && (
                <div style={{
                  position: 'absolute',
                  top: '-1px',
                  left: '0',
                  right: '0',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #00ffff 0%, #00cccc 100%)',
                    color: '#000',
                    fontWeight: '700',
                    padding: '0.4rem 1.5rem',
                    fontSize: '0.75rem',
                    borderRadius: '0 0 12px 12px',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    ⭐ Mais Popular
                  </div>
                </div>
              )}

              {/* Current Plan Badge */}
              {isCurrent && (
                <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem' }}>
                  <Badge variant="outline" style={{ 
                    borderColor: '#059669', 
                    color: '#059669',
                    fontSize: '0.65rem',
                    padding: '0.2rem 0.5rem'
                  }}>
                    Atual
                  </Badge>
                </div>
              )}

              {/* Plan Header */}
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '0.75rem', 
                marginTop: isPopular ? '1.5rem' : '0' 
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                  color: '#fff',
                  background: getPlanGradient(plan.id)
                }}>
                  {getPlanIcon(plan.id)}
                </div>
                
                <h2 style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '700', 
                  color: '#ffffff',
                  marginBottom: '0.25rem'
                }}>
                  {plan.name}
                </h2>
              </div>

              {/* Price */}
              <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                {isFree ? (
                  <div style={{ 
                    fontSize: '1.8rem', 
                    fontWeight: '800', 
                    color: '#888'
                  }}>
                    Grátis
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
                    <span style={{ color: '#888', fontSize: '0.9rem' }}>R$</span>
                    <span style={{ 
                      fontSize: '2rem', 
                      fontWeight: '800', 
                      color: '#00ffff'
                    }}>
                      {plan.price.toFixed(2).replace('.', ',')}
                    </span>
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>/mês</span>
                  </div>
                )}
              </div>

              {/* Credits Badge */}
              <div style={{
                background: '#0f0f0f',
                borderRadius: '6px',
                padding: '0.5rem',
                textAlign: 'center',
                marginBottom: '0.75rem',
                fontSize: '0.8rem'
              }}>
                <span style={{ color: '#00ffff', fontWeight: '600' }}>
                  🎯 {plan.unlimited_credits 
                    ? 'Créditos Ilimitados' 
                    : `${plan.monthly_credits} créditos/mês`}
                </span>
                {plan.daily_login_bonus > 0 && !plan.unlimited_credits && (
                  <span style={{ color: '#888', fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>
                    +{plan.daily_login_bonus} bônus por login diário
                  </span>
                )}
              </div>

              {/* Includes Previous Plan */}
              {includesText && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(0, 200, 200, 0.05) 100%)',
                  border: '1px solid rgba(0, 255, 255, 0.2)',
                  borderRadius: '8px',
                  padding: '0.6rem',
                  marginBottom: '0.75rem',
                  textAlign: 'center'
                }}>
                  <span style={{ color: '#00ffff', fontSize: '0.8rem', fontWeight: '600' }}>
                    ✓ Tudo do plano {includesText}
                  </span>
                </div>
              )}

              {/* Exclusive Features Preview */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.35rem',
                marginBottom: '0.5rem',
                flex: 1
              }}>
                <p style={{ 
                  color: '#888', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem'
                }}>
                  {includesText ? 'Benefícios exclusivos:' : 'Benefícios:'}
                </p>
                
                {exclusiveFeatures.slice(0, isExpanded ? exclusiveFeatures.length : 3).map((feature) => (
                  <div key={feature} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#e0e0e0'
                  }}>
                    <Check className="w-3 h-3" style={{ color: '#059669', flexShrink: 0 }} />
                    <span>{FEATURE_LABELS[feature] || feature}</span>
                  </div>
                ))}

                {/* Expanded: Show all inherited features */}
                {isExpanded && includesText && (
                  <>
                    <div style={{ 
                      borderTop: '1px solid #333', 
                      margin: '0.5rem 0',
                      paddingTop: '0.5rem'
                    }}>
                      <p style={{ 
                        color: '#888', 
                        fontSize: '0.7rem', 
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.25rem'
                      }}>
                        + Todos do plano {includesText}:
                      </p>
                      {getAllFeatures(PLAN_INCLUDES[plan.id]?.toLowerCase() || 'free').map((feature) => (
                        <div key={feature} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          fontSize: '0.7rem',
                          color: '#888'
                        }}>
                          <Check className="w-3 h-3" style={{ color: '#4b5563', flexShrink: 0 }} />
                          <span>{FEATURE_LABELS[feature] || feature}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Expand/Collapse Button */}
              {exclusiveFeatures.length > 3 || includesText ? (
                <button
                  onClick={() => toggleExpand(plan.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#00ffff',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem',
                    marginBottom: '0.5rem',
                    width: '100%'
                  }}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Ver menos
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Ver todos os benefícios
                    </>
                  )}
                </button>
              ) : (
                <div style={{ marginBottom: '0.5rem' }} />
              )}

              {/* Subscribe Button */}
              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={checkoutLoading === plan.id || isCurrent || isFree}
                style={{
                  width: '100%',
                  padding: '0.7rem',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                  background: isFree 
                    ? '#333'
                    : isPopular 
                      ? 'linear-gradient(135deg, #00ffff 0%, #00cccc 100%)'
                      : '#333',
                  color: isPopular && !isFree ? '#000' : '#fff',
                  border: 'none',
                  cursor: isCurrent || isFree ? 'not-allowed' : 'pointer',
                  opacity: isCurrent || isFree ? 0.6 : 1,
                  marginTop: 'auto'
                }}
              >
                {checkoutLoading === plan.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrent ? (
                  'Plano Atual'
                ) : isFree ? (
                  'Plano Gratuito'
                ) : (
                  'Assinar'
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Credits & Consultancy Purchase Section */}
      {user && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
          marginTop: '2rem',
          maxWidth: '800px',
          margin: '2rem auto 0'
        }}>
          {/* Buy Credits Card */}
          <div style={{
            background: '#1a1a1a',
            border: '2px solid #333',
            borderRadius: '12px',
            padding: '1.25rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CreditCard className="w-5 h-5" style={{ color: '#fff' }} />
              </div>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#fff', margin: 0 }}>
                  Comprar Créditos
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  R$ 2,00 por crédito
                </p>
              </div>
            </div>

            {/* Current Credits Info */}
            {subscriptionStatus && (
              <div style={{
                background: '#0f0f0f',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.8rem'
              }}>
                <span style={{ color: '#888' }}>Seu saldo: </span>
                <span style={{ color: '#00ffff', fontWeight: '600' }}>
                  {subscriptionStatus.unlimited_credits 
                    ? 'Ilimitado' 
                    : `${subscriptionStatus.total_credits} créditos`}
                </span>
              </div>
            )}

            {/* Quantity Selector */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <button
                onClick={() => setCreditsQuantity(Math.max(1, creditsQuantity - 5))}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#333',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Minus className="w-4 h-4" />
              </button>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#22c55e' }}>
                  {creditsQuantity}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#888' }}>créditos</div>
              </div>
              
              <button
                onClick={() => setCreditsQuantity(creditsQuantity + 5)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#333',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Total Price */}
            <div style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: '0.5rem',
              background: 'rgba(34, 197, 94, 0.1)',
              borderRadius: '8px'
            }}>
              <span style={{ color: '#888', fontSize: '0.8rem' }}>Total: </span>
              <span style={{ color: '#22c55e', fontWeight: '700', fontSize: '1.1rem' }}>
                R$ {(creditsQuantity * 2).toFixed(2).replace('.', ',')}
              </span>
            </div>

            <Button
              onClick={handleBuyCredits}
              disabled={checkoutLoading === 'credits'}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: '#fff',
                border: 'none',
                fontWeight: '600'
              }}
              data-testid="buy-credits-btn"
            >
              {checkoutLoading === 'credits' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Comprar Créditos'
              )}
            </Button>
          </div>

          {/* Buy Consultancy Card */}
          <div style={{
            background: '#1a1a1a',
            border: '2px solid #333',
            borderRadius: '12px',
            padding: '1.25rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Video className="w-5 h-5" style={{ color: '#fff' }} />
              </div>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#fff', margin: 0 }}>
                  Consultoria Humana
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  R$ 150,00 por sessão (1h15min)
                </p>
              </div>
            </div>

            {/* Current Sessions Info */}
            {subscriptionStatus && (
              <div style={{
                background: '#0f0f0f',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.8rem'
              }}>
                <span style={{ color: '#888' }}>Sessões disponíveis: </span>
                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>
                  {subscriptionStatus.consultancy_sessions || 0}
                </span>
              </div>
            )}

            {/* Sessions Selector */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <button
                onClick={() => setConsultancySessions(Math.max(1, consultancySessions - 1))}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#333',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Minus className="w-4 h-4" />
              </button>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>
                  {consultancySessions}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#888' }}>sessão(ões)</div>
              </div>
              
              <button
                onClick={() => setConsultancySessions(consultancySessions + 1)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#333',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Total Price */}
            <div style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: '0.5rem',
              background: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '8px'
            }}>
              <span style={{ color: '#888', fontSize: '0.8rem' }}>Total: </span>
              <span style={{ color: '#8b5cf6', fontWeight: '700', fontSize: '1.1rem' }}>
                R$ {(consultancySessions * 150).toFixed(2).replace('.', ',')}
              </span>
            </div>

            <Button
              onClick={handleBuyConsultancy}
              disabled={checkoutLoading === 'consultancy'}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: '#fff',
                border: 'none',
                fontWeight: '600'
              }}
              data-testid="buy-consultancy-btn"
            >
              {checkoutLoading === 'consultancy' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Comprar Consultoria'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '1.5rem',
        marginTop: '1.5rem',
        padding: '0.75rem',
        background: '#0f0f0f',
        borderRadius: '8px',
        color: '#888',
        fontSize: '0.8rem',
        flexWrap: 'wrap'
      }}>
        <span>💳 Pagamento seguro</span>
        <span>🔄 Cancele quando quiser</span>
        <span>📧 Suporte por email</span>
      </div>
      <Footer />
    </div>
  );
};

export default PricingPage;
